
import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
    MdAdd,
    MdVisibility,
    MdEdit,
    MdDelete,
    MdMoreVert,
    MdSearch
} from 'react-icons/md';
import './Products.css';
import '../pages/Dashboard.css';
import Swal from 'sweetalert2';
import axios from 'axios';
import { GET_ALL_PRODUCTS_API, DELETE_PRODUCT_API } from '../config/API';
import Loader from '../components/common/Loader';
import NoData from '../components/common/NoData';
import Pagination from '../components/common/Pagination';
import TableLimitSelector from '../components/common/TableLimitSelector';
import ActionMenuPortal from '../components/common/ActionMenuPortal';
import { useUser } from '../context/UserContext';

const Products = () => {
    const navigate = useNavigate();
    const { hasPermission } = useUser();
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [totalPages, setTotalPages] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);
    const [openMenuId, setOpenMenuId] = useState(null);
    const [anchorRect, setAnchorRect] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');

    // Debounce search input
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchTerm);
        }, 500);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    // Reset to page 1 when search changes
    useEffect(() => {
        setCurrentPage(1);
    }, [debouncedSearch]);

    const fetchProducts = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('accessToken');
            const response = await axios.get(GET_ALL_PRODUCTS_API, {
                params: {
                    page: currentPage,
                    limit: limit,
                    search: debouncedSearch || undefined
                },
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.status) {
                setProducts(response.data.data || []);
                const pagination = response.data.pagination || {};
                setTotalPages(pagination.totalPages || response.data.totalPages || 1);
                setTotalRecords(pagination.total || pagination.totalRecords || response.data.totalRecords || 0);
            } else {
                console.error('Failed to fetch products:', response.data.message);
            }
        } catch (error) {
            console.error('Error fetching products:', error);
            // Swal.fire('Error', 'Failed to fetch products', 'error');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchProducts();
    }, [currentPage, debouncedSearch, limit]);

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handleLimitChange = (newLimit) => {
        setLimit(newLimit);
        setCurrentPage(1);
    };

    const toggleMenu = (id, e) => {
        e.stopPropagation();
        if (openMenuId === id) {
            setOpenMenuId(null);
            setAnchorRect(null);
        } else {
            setOpenMenuId(id);
            setAnchorRect(e.currentTarget.getBoundingClientRect());
        }
    };

    // Close menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (event.target.closest('.swal2-container')) return;
            if (event.target.closest('.action-portal-wrapper')) return;
            if (event.target.closest('.action-menu-btn')) return;

            setOpenMenuId(null);
            setAnchorRect(null);
        };

        document.addEventListener('click', handleClickOutside, true);
        return () => {
            document.removeEventListener('click', handleClickOutside, true);
        };
    }, []);

    const handleDelete = async (id) => {
        setOpenMenuId(null);
        const result = await Swal.fire({
            title: 'Delete Product?',
            text: 'Are you sure you want to delete this product?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, Delete',
            cancelButtonText: 'Cancel',
            confirmButtonColor: '#dc2626',
            buttonsStyling: false,
            customClass: {
                confirmButton: 'swal-delete-btn',
                cancelButton: 'swal-cancel-btn',
            },
        });

        if (result.isConfirmed) {
            try {
                const token = localStorage.getItem('accessToken');
                const response = await axios.delete(DELETE_PRODUCT_API(id), {
                    headers: { Authorization: `Bearer ${token}` }
                });

                if (response.data.status) {
                    Swal.fire({
                        icon: 'success',
                        title: 'Deleted!',
                        text: 'Product has been deleted.',
                        timer: 1500,
                        showConfirmButton: false
                    });
                    
                    // Optimistic update
                    setProducts(prev => prev.filter(product => (product.id || product._id) !== id));
                    
                    // If it was the last item on the page and not on page 1, go to previous page
                    if (products.length === 1 && currentPage > 1) {
                        setCurrentPage(prev => prev - 1);
                    } else {
                        fetchProducts();
                    }
                } else {
                    Swal.fire('Error', response.data.error || response.data.message || 'Failed to delete product', 'error');
                }
            } catch (error) {
                console.error('Error deleting product:', error);
                Swal.fire('Error', error.response?.data?.error || error.response?.data?.message || 'An error occurred while deleting', 'error');
            }
        }
    };

    return (
        <div className="dashboard">
            <div className="products-header">
                <h2 className="section-title" style={{ margin: 0 }}>Products</h2>
                {hasPermission('PRODUCTS', 'create') && (
                    <Link to="/products/new" className="add-product-btn">
                        <MdAdd size={20} />
                        Add Product
                    </Link>
                )}
            </div>

            <div className="card-container-products">
                {/* Search Bar */}
                <div style={{ padding: '1rem', borderBottom: '1px solid #e5e7eb', background: '#f9fafb' }}>
                    <div style={{ position: 'relative', maxWidth: '500px' }}>
                        <MdSearch
                            style={{
                                position: 'absolute',
                                left: '12px',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                color: '#9ca3af',
                                fontSize: '1.25rem'
                            }}
                        />
                        <input
                            type="text"
                            placeholder="Search products..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            style={{
                                width: '100%',
                                padding: '0.625rem 1rem 0.625rem 2.5rem',
                                border: '1px solid #d1d5db',
                                borderRadius: '8px',
                                fontSize: '0.875rem',
                                outline: 'none',
                                transition: 'border-color 0.2s',
                                background: 'white'
                            }}
                            onFocus={(e) => e.target.style.borderColor = '#4f46e5'}
                            onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                        />
                        {searchTerm && (
                            <button
                                onClick={() => setSearchTerm('')}
                                style={{
                                    position: 'absolute',
                                    right: '10px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    background: '#e5e7eb',
                                    border: 'none',
                                    borderRadius: '50%',
                                    width: '20px',
                                    height: '20px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                    color: '#4b5563',
                                    fontSize: '12px'
                                }}
                            >
                                ✕
                            </button>
                        )}
                    </div>
                </div>
                {loading ? (
                    <Loader text="Fetching products..." />
                ) : products.length === 0 ? (
                    <NoData text="No products found. Click 'Add Product' to create one." />
                ) : (
                    <>
                        <div className="table-container">
                            <table>

                                <thead>
                                    <tr>
                                        <th>Product Name</th>
                                        <th>Category</th>
                                        <th>Unit</th>
                                        <th>Default Rate (₹)</th>
                                        <th style={{ width: '250px' }}>Description</th>
                                        <th style={{ textAlign: 'right' }}>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {products.map((product, index) => (
                                        <tr key={product._id || product.id}>
                                            <td>
                                                <strong>{product.productName}</strong>
                                            </td>
                                            <td>
                                                <span className="category-badge">
                                                    {product.ProductCategory?.categoryName || 'No Category'}
                                                </span>
                                            </td>
                                            <td>{product.Unit?.unitName || '-'}</td>
                                            <td>₹{product.defaultRate || product.rate || '-'}</td>
                                            <td>
                                                <div className="product-description-cell">
                                                    {product.description || '-'}
                                                </div>
                                            </td>
                                            <td className={`action-cell ${openMenuId === (product._id || product.id) ? 'menu-open' : ''}`}>
                                                <button
                                                    className="action-menu-btn"
                                                    onClick={(e) => toggleMenu(product._id || product.id, e)}
                                                    title="Actions"
                                                >
                                                    <MdMoreVert size={20} />
                                                </button>

                                                <ActionMenuPortal
                                                    isOpen={openMenuId === (product._id || product.id)}
                                                    anchorRect={anchorRect}
                                                    onClose={() => setOpenMenuId(null)}
                                                >
                                                    <div className="action-dropdown">
                                                        {hasPermission('PRODUCTS', 'read') && (
                                                            <button className="action-item" onClick={() => navigate(`/products/edit/${product._id || product.id}`)}>
                                                                <MdVisibility size={16} /> View Details
                                                            </button>
                                                        )}
                                                        {hasPermission('PRODUCTS', 'update') && (
                                                            <button className="action-item" onClick={() => navigate(`/products/edit/${product._id || product.id}`)}>
                                                                <MdEdit size={16} /> Edit Product
                                                            </button>
                                                        )}
                                                        {hasPermission('PRODUCTS', 'delete') && (
                                                            <button
                                                                className="action-item delete"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleDelete(product._id || product.id);
                                                                }}
                                                            >
                                                                <MdDelete size={16} /> Delete
                                                            </button>
                                                        )}
                                                    </div>
                                                </ActionMenuPortal>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Mobile Card View */}
                        <div className="mobile-card-view">

                            {products.map((product) => (
                                <div key={product._id || product.id} className="product-card">
                                    <div className="card-header">
                                        <div className="card-title">
                                            <strong>{product.productName}</strong>
                                            <span className="category-badge">{product.ProductCategory?.categoryName || 'No Category'}</span>
                                        </div>
                                        <button
                                            className="action-menu-btn"
                                            onClick={(e) => toggleMenu(product._id || product.id, e)}
                                            title="Actions"
                                        >
                                            <MdMoreVert size={20} />
                                        </button>
                                        <ActionMenuPortal
                                            isOpen={openMenuId === (product._id || product.id)}
                                            anchorRect={anchorRect}
                                            onClose={() => setOpenMenuId(null)}
                                        >
                                            <div className="action-dropdown shadow-lg">
                                                {hasPermission('PRODUCTS', 'read') && (
                                                    <button className="action-item" onClick={() => navigate(`/products/edit/${product._id || product.id}`)}>
                                                        <MdVisibility size={16} /> View Details
                                                    </button>
                                                )}
                                                {hasPermission('PRODUCTS', 'update') && (
                                                    <button className="action-item" onClick={() => navigate(`/products/edit/${product._id || product.id}`)}>
                                                        <MdEdit size={16} /> Edit Product
                                                    </button>
                                                )}
                                                {hasPermission('PRODUCTS', 'delete') && (
                                                    <button
                                                        className="action-item delete"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleDelete(product._id || product.id);
                                                        }}
                                                    >
                                                        <MdDelete size={16} /> Delete
                                                    </button>
                                                )}
                                            </div>
                                        </ActionMenuPortal>
                                    </div>
                                    <div className="card-body">
                                        <div className="card-row description-row">
                                            <span className="card-label">Description:</span>
                                            <span className="card-value">{product.description || '-'}</span>
                                        </div>
                                        <div className="card-row">
                                            <span className="card-label">Unit:</span>
                                            <span className="card-value">{product.Unit?.unitName || '-'}</span>
                                        </div>
                                        <div className="card-row">
                                            <span className="card-label">Default Rate:</span>
                                            <span className="card-value">₹{product.defaultRate || product.rate}</span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <Pagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            onPageChange={handlePageChange}
                            totalRecords={totalRecords}
                            currentCount={products.length}
                            entityName="Products"
                         limit={limit} onLimitChange={handleLimitChange} />
                    </>
                )}
            </div>
        </div>
    );
};

export default Products;

